import { VanillaSelectionArea as VanillaSelectionAreaMistyped } from '@viselect/react';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ForwardedRef } from 'react';
import { useForceRerender } from '@/shared/lib/customHooks';
import type { SelectionAreaWrapperHandle, SelectionAreaWrapperProps } from './index';

// @viselect/react's own shipped .d.ts aliases `VanillaSelectionArea` to the same
// symbol as its default export (the React component) - a bug in the package's
// declaration file. At runtime it is the real imperative vanilla class.
const VanillaSelectionArea = VanillaSelectionAreaMistyped as unknown as new (opt: {
  boundaries?: Element | Element[] | string;
  selectables?: string;
}) => any;

const allowedButtons = [
  1, // left click
];

/**
 * Wires a vanilla selection-area over a beat grid so beats can be marquee-dragged, ctrl-clicked or keyboard-selected. The area is constructed once against the container ref and its beforestart, start, move and stop events maintain a ref-held id set, further effects re-select a default beat, publish the set when a drag ends, register the ctrl-A and escape shortcuts, enable or disable the area while loading or empty, and force a reset when the owner reports an empty selection.
 *
 * @param props - Container id, selectables, loading and empty flags, default selection and the selection callback.
 * @param ref - Forwarded ref receiving the resetSelection handle.
 * @returns An object exposing the container ref as root and the selected id set.
 */
const useSelectionAreaWrapper = (
  {
    id,
    handleSetSelected,
    className,
    selectables,
    isNoData,
    isLoading,
    couldForceReset = false,
    defaultSelectedId,
    isEmptySelected,
    children,
  }: SelectionAreaWrapperProps,
  ref: ForwardedRef<SelectionAreaWrapperHandle>,
) => {
  const selected = useRef(new Set<string>());
  const moveCounter = useRef(0);
  const defaultSelectedIdRef = useRef<string>();
  const callBackSetSelected = useRef(handleSetSelected);
  const forceRender = useForceRerender();
  const [isSelecting, setIsSelecting] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const selectionArea = useRef<any>();

  const isDisabled = isNoData || isLoading;

  /**
   * Reads the DOM ids off a set of selected beat elements. Elements without an id attribute are dropped and the remainder coerced to strings.
   *
   * @param els - The DOM elements to read ids from.
   * @returns The ids of the elements that carry one.
   */
  const extractIds = (els: Element[]) =>
    els
      .map((v) => v.getAttribute('id'))
      .filter(Boolean)
      .map(String);

  /**
   * Decides whether a drag on the beat grid may begin and resets the selection for a plain drag. Presses from buttons outside the allowed list are refused, a drag without ctrl, meta or shift clears both the library selection and the local id set and notifies the owner, and any default selected beat is re-selected before a re-render is forced.
   *
   * @param event - Pointer event that started the drag.
   * @param selection - The selection-area instance for this drag.
   * @returns False when the pressed button is not allowed, undefined when the drag may proceed.
   */
  const onBeforeStart = ({ event, selection }: any) => {
    if (!allowedButtons.includes(event?.buttons)) return false;
    if (!event?.ctrlKey && !event?.metaKey && !event?.shiftKey) {
      // Click inside container to clear
      selection.clearSelection();
      selected.current = new Set();
      callBackSetSelected.current(selected.current);
    }
    if (defaultSelectedIdRef.current) {
      selection.select([`#${defaultSelectedIdRef.current}`]);
    }
    forceRender();
    return undefined;
  };

  /**
   * Begins a marquee drag over the beat grid. The move counter is reset, a drag without ctrl, meta or shift clears the library selection and the local id set, a re-render is forced and the selecting flag is raised.
   *
   * @param event - Pointer event that started the drag.
   * @param selection - The selection-area instance for this drag.
   * @returns Nothing.
   */
  const onStart = ({ event, selection }: any) => {
    moveCounter.current = 0;
    if (!event?.ctrlKey && !event?.metaKey && !event?.shiftKey) {
      selection.clearSelection();
      selected.current = new Set();
    }
    forceRender();
    setIsSelecting(true);
  };

  const onMove = ({
    store: {
      changed: { added, removed },
    },
  }: any) => {
    moveCounter.current += 1;
    if (added.length > 0 || removed.length > 0) {
      const currentSelected = new Set(selected.current);
      extractIds(added).forEach((id) => currentSelected.add(id));
      extractIds(removed).forEach((id) => currentSelected.delete(id));
      selected.current = currentSelected;
    }
  };

  /**
   * Finishes a marquee drag and publishes the resulting beat selection. A single-move drag without a modifier key is treated as a bare click and clears everything, otherwise the accumulated id set is handed to the owner, and either way the selecting flag is lowered.
   *
   * @param event - Pointer event that ended the drag.
   * @param selection - The selection-area instance for this drag.
   * @returns Nothing.
   */
  const onStop = ({ event, selection }: any) => {
    if (moveCounter.current === 1 && !event?.ctrlKey && !event?.metaKey && !event?.shiftKey) {
      selection.clearSelection();
      selected.current = new Set();
      forceRender();
    } else {
      callBackSetSelected.current(selected.current);
    }
    setIsSelecting(false);
  };

  useEffect(() => {
    callBackSetSelected.current = handleSetSelected;
  }, [handleSetSelected]);

  useEffect(() => {
    selectionArea.current = new VanillaSelectionArea({
      boundaries: root.current,
      selectables,
    });
    selectionArea.current.on('beforestart', onBeforeStart);
    selectionArea.current.on('start', onStart);
    selectionArea.current.on('move', onMove);
    selectionArea.current.on('stop', onStop);
    return () => selectionArea.current.destroy();
  }, []);

  useEffect(() => {
    if (defaultSelectedId && selectionArea.current) {
      defaultSelectedIdRef.current = `beat-${defaultSelectedId}`;
      selectionArea.current.clearSelection();
      selected.current = new Set();
      selectionArea.current.select([`#${defaultSelectedIdRef.current}`], true);
      callBackSetSelected.current(selected.current);
      forceRender();
    }
  }, [defaultSelectedId]);

  useEffect(() => {
    if (!isSelecting) {
      callBackSetSelected.current(selected.current);
    }
  }, [isSelecting]);

  useImperativeHandle(
    ref,
    () => ({
      /**
       * Clears the beat selection on demand from the parent component. The selection-area instance is cleared, the local id set replaced with an empty one, and a re-render forced without notifying the selection callback.
       *
       * @returns Nothing.
       */
      resetSelection() {
        selectionArea.current.clearSelection();
        selected.current = new Set();
        forceRender();
      },
    }),
    [],
  );

  useEffect(() => {
    /**
     * Adds keyboard shortcuts for selecting or clearing every beat in the grid. Auto-repeat is ignored, ctrl or meta with A selects all beat buttons under the container id and merges their ids into the current set, escape clears the selection outright, and both paths notify the owner and force a re-render.
     *
     * @param event - Keydown event observed on the window.
     * @returns Nothing.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event?.repeat) return;
      const { keyCode, ctrlKey, metaKey } = event;
      if ((ctrlKey || metaKey) && keyCode === 65) {
        // CTRL + A
        event.preventDefault();
        const elList = document.querySelectorAll(`#${id} button[id^="beat-"]`);
        selectionArea.current.select([`#${id} button[id^="beat-"]`], true);
        const currentSelected = new Set(selected.current);
        extractIds([...elList]).forEach((id) => currentSelected.add(id));
        selected.current = currentSelected;
        callBackSetSelected.current(selected.current);
        forceRender();
        return;
      }
      if (keyCode === 27) {
        // ESC
        event.preventDefault();
        selectionArea.current.clearSelection(true);
        selected.current = new Set();
        callBackSetSelected.current(selected.current);
        forceRender();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (selectionArea.current) {
      if (isDisabled) {
        selectionArea.current.disable();
      } else {
        selectionArea.current.enable();
      }
    }
  }, [isDisabled]);

  useEffect(() => {
    if (couldForceReset && isEmptySelected) {
      selectionArea.current.clearSelection(true);
      selected.current = new Set();
      callBackSetSelected.current(selected.current);
      forceRender();
    }
  }, [couldForceReset, isEmptySelected]);

  return { root, selected };
};

export default useSelectionAreaWrapper;
